import React, { useState } from 'react';
import posthog from 'posthog-js';
import { Icon } from './ui/Icon.jsx';
import { LangToggle } from './ui/LangToggle.jsx';
import ImageUploader from './ImageUploader.jsx';
import { useMyVendorProducts, useSaveVendorProduct, useBulkSaveVendorProducts, useBulkUpdateVendorProducts, useDeleteVendorProducts } from '../hooks/useVendors.js';
import { COLORS, FONT, input, button, cardBox, APPROVAL_STATUS } from '../lib/theme.js';
import { uid, fmt } from '../lib/utils.js';
import { CATEGORY_TAXONOMY, catLabel } from '../data/categories.js';
import { LOGO_URL } from '../lib/constants.js';
import { parseCSV, decodeCsvBuffer, downloadCsv } from '../lib/csv.js';

const EMPTY = {
  name_en: '', name_ar: '', sku: '', category: '', price: '', min_qty: 1, stock: 0,
  image: '', images: [], carton_qty: '', category_ar: '', description: '', description_ar: '',
};
// Vendors must categorise against the fixed taxonomy (same as the single-add
// select), so batch rows are validated against these ids.
const VALID_CATEGORY_IDS = new Set(CATEGORY_TAXONOMY.map(c => c.id));

// ─── VENDOR PORTAL ────────────────────────────────────────────────────────────
// What an approved vendor sees after signing in at /admin.
// They manage only their own products; everything they add, edit, or bulk-import
// goes back to "pending" until Sedra staff approve it in the Vendors tab.
//
// A super admin can also open this exact component via "View as vendor"
// (impersonatedBy set) — the vendor's OWN screen, never a re-implementation, so
// every feature added here works for both. Writes go through the staff JWT,
// which staff_all_products already permits.
export default function VendorPortal({ vendor, lang, setLang, onLogout, impersonatedBy, onExitImpersonation }) {
  const ar  = lang === 'ar';
  const dir = ar ? 'rtl' : 'ltr';
  const { data: products = [], isLoading } = useMyVendorProducts(vendor.id);
  const saveProduct = useSaveVendorProduct(vendor.id);
  const bulkSave    = useBulkSaveVendorProducts(vendor.id);
  const bulkUpdate  = useBulkUpdateVendorProducts(vendor.id);
  const deleteMany  = useDeleteVendorProducts(vendor.id);

  const [form, setForm]       = useState(null);   // null = list view; object = editing
  const [err, setErr]         = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [importStage, setImportStage] = useState(null); // { newRows, updateRows, errorRows }
  const [importBusy, setImportBusy]   = useState(false);
  const [lastImport, setLastImport]   = useState(null); // { newIds } — undo target
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEdit, setBulkEdit]       = useState(null);  // 'category' | 'price'
  const [bulkValue, setBulkValue]     = useState('');

  const vendorName = (ar && vendor.business_name_ar) ? vendor.business_name_ar : vendor.business_name_en;
  const suspended  = vendor.status !== 'active';

  const live    = products.filter(p => p.approval_status === 'approved');
  const waiting  = products.filter(p => p.approval_status === 'pending');

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setSavedMsg('');
    try {
      const wasNew = !form.id;
      const row = {
        ...form,
        price:   Number(form.price) || 0,
        min_qty: Number(form.min_qty) || 1,
        stock:   Number(form.stock) || 0,
        // Optional columns: '' would be rejected by an integer column and
        // stored as an empty string on the text ones — send NULL instead.
        carton_qty:     form.carton_qty === '' || form.carton_qty == null ? null : Number(form.carton_qty),
        category_ar:    (form.category_ar || '').trim() || null,
        description:    (form.description || '').trim() || null,
        description_ar: (form.description_ar || '').trim() || null,
        is_active: true,
      };
      if (!row.id) { row.id = uid(); row._isNew = true; row.created_at = new Date().toISOString(); }
      await saveProduct.mutateAsync(row);
      posthog.capture('vendor_product_submitted', {
        vendor_id: vendor.id,
        product_id: row.id,
        operation: wasNew ? 'create' : 'update',
        category: row.category,
      });
      setForm(null);
      setSavedMsg(ar ? 'تم الإرسال — سيظهر المنتج في الكتالوج بعد موافقة سدرة هوم' : 'Submitted — your product goes live once Sedra Home approves it');
    } catch (e2) {
      posthog.captureException(e2, { flow: 'vendor_product_submission' });
      setErr(e2.message);
    }
  };

  // ── CSV → staging (vendor-scoped, no DB write yet) ──
  const handleCSV = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = '';
    setErr(''); setSavedMsg('');
    let parsed;
    try { parsed = parseCSV(decodeCsvBuffer(await file.arrayBuffer())); }
    catch (e2) { setErr((ar ? 'تعذّر قراءة الملف: ' : 'Could not read the file: ') + e2.message); return; }
    if (!parsed.length) { setErr(ar ? 'لم يتم العثور على صفوف صالحة في الملف' : 'No valid rows found in the CSV'); return; }

    // SKU map from THIS vendor's own products only — never global, so a batch
    // can't collide with or overwrite another seller's SKU.
    const skuMap = {};
    products.forEach(p => { if (p.sku) skuMap[p.sku] = p; });

    const newRows = [], updateRows = [], errorRows = [];
    for (const r of parsed) {
      const price = parseFloat(r.price);
      const category = (r.category || '').trim().toLowerCase();
      if (!r.sku || isNaN(price) || price <= 0 || !VALID_CATEGORY_IDS.has(category)) {
        errorRows.push(r);
        continue;
      }
      const existing = skuMap[r.sku];
      const row = {
        id:         existing?.id || uid(),
        name_en:    r.name_en || '',
        name_ar:    r.name_ar || '',
        sku:        r.sku,
        category,
        price,
        min_qty:    parseInt(r.min_qty) || 1,
        stock:      parseInt(r.stock) || 0,
        image:      r.image_url || r.image || '',
        images:     r.images ? r.images.split('|').map(s => s.trim()).filter(Boolean) : [],
        // Parity with the admin importer's field set.
        carton_qty:     parseInt(r.carton_qty) || null,
        category_ar:    (r.category_ar || '').trim() || null,
        description:    (r.description || '').trim() || null,
        description_ar: (r.description_ar || '').trim() || null,
        created_at: existing?.created_at || new Date().toISOString(),
      };
      (existing ? updateRows : newRows).push(row);
    }
    setImportStage({ newRows, updateRows, errorRows });
  };

  const confirmImport = async () => {
    if (!importStage) return;
    const { newRows, updateRows } = importStage;
    if (!newRows.length && !updateRows.length) { setImportStage(null); return; }
    setImportBusy(true);
    try {
      await bulkSave.mutateAsync({ newRows, updateRows });
      const n = newRows.length + updateRows.length;
      // Only newly-inserted rows are undoable (updates overwrote prior data we
      // no longer have) — matches the admin importer's revert scope.
      setLastImport({ newIds: newRows.map(r => r.id), updateCount: updateRows.length });
      setImportStage(null);
      setSavedMsg(ar
        ? `تم إرسال ${n} منتج للمراجعة — ستظهر في الكتالوج بعد موافقة سدرة هوم`
        : `${n} product(s) submitted for review — they go live once Sedra Home approves them`);
    } catch (e2) { setErr(e2.message); }
    setImportBusy(false);
  };

  // ── undo the last import (delete only its newly-inserted products) ──
  const undoImport = async () => {
    if (!lastImport?.newIds?.length) { setLastImport(null); return; }
    const n = lastImport.newIds.length;
    const msg = lastImport.updateCount > 0
      ? (ar
          ? `سيتم حذف ${n} منتج جديد نهائياً. ${lastImport.updateCount} منتج تم تحديثه لا يمكن التراجع عنه تلقائياً.`
          : `${n} newly-added product(s) will be permanently deleted. ${lastImport.updateCount} updated product(s) cannot be auto-reverted.`)
      : (ar ? `حذف ${n} منتج من آخر استيراد نهائياً؟` : `Permanently delete ${n} product(s) from the last import?`);
    if (!window.confirm(msg)) return;
    setErr(''); setSavedMsg('');
    try {
      await deleteMany.mutateAsync(lastImport.newIds);
      setLastImport(null);
      setSavedMsg(ar ? 'تم التراجع عن الاستيراد' : 'Import reverted');
    } catch (e2) { setErr(e2.message); }
  };

  // ── bulk show/hide selected own products ──
  const toggleSelect = (id, on) => setSelectedIds(s => { const ns = new Set(s); on ? ns.add(id) : ns.delete(id); return ns; });
  const bulkSetActive = async (active) => {
    if (!selectedIds.size) return;
    setErr(''); setSavedMsg('');
    const n = selectedIds.size;
    try {
      await bulkUpdate.mutateAsync({ ids: selectedIds, patch: { is_active: active } });
      setSelectedIds(new Set());
      setSavedMsg(active
        ? (ar ? `تم إظهار ${n} منتج` : `${n} product(s) shown`)
        : (ar ? `تم إخفاء ${n} منتج` : `${n} product(s) hidden`));
    } catch (e2) { setErr(e2.message); }
  };

  // ── bulk category / price edit ──
  // Both are CONTENT changes, so the re-approval trigger sends every touched
  // row back to the review queue — which is the intended behaviour.
  const bulkEditField = async (patch, label) => {
    if (!selectedIds.size) return;
    setErr(''); setSavedMsg('');
    const n = selectedIds.size;
    try {
      await bulkUpdate.mutateAsync({ ids: selectedIds, patch });
      setSelectedIds(new Set());
      setBulkEdit(null);
      setSavedMsg(ar
        ? `تم تحديث ${label} لـ ${n} منتج — بانتظار المراجعة`
        : `${label} updated on ${n} product(s) — back in the review queue`);
    } catch (e2) { setErr(e2.message); }
  };

  const downloadTemplate = () => {
    // `images` is pipe-delimited extra gallery URLs; image_url is the cover.
    // Field set matches the admin importer, so a sheet works in either place.
    const csv = 'name_en,name_ar,sku,category,category_ar,price,min_qty,carton_qty,stock,description,description_ar,image_url,images\n'
      + 'Example Product,اسم المنتج,SKU001,cookware,أواني الطهي,150,6,12,0,Short description,وصف قصير,https://example.com/cover.jpg,https://example.com/2.jpg|https://example.com/3.jpg';
    downloadCsv('sedra_vendor_products_template.csv', csv);
  };

  const chip = (status) => {
    const s = APPROVAL_STATUS[status] || APPROVAL_STATUS.pending;
    return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, color: s.color, background: s.bg }}>{ar ? s.label_ar : s.label_en}</span>;
  };

  return (
    <div style={{ minHeight: '100vh', background: COLORS.page, fontFamily: FONT, direction: dir }}>
      {/* Top bar */}
      <div style={{ background: COLORS.brandDeep, color: '#fff', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={LOGO_URL} alt="Sedra Home" style={{ height: 34, borderRadius: 6 }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="Store" size={14} /> {vendorName}</div>
            <div style={{ fontSize: 10.5, opacity: 0.75 }}>{ar ? 'بوابة البائع — سدرة هوم' : 'Vendor Portal — Sedra Home'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <LangToggle lang={lang} setLang={setLang} variant="dark" size="sm" />
          {impersonatedBy ? (
            <button onClick={onExitImpersonation} style={{ background: 'var(--sedra-tan)', border: 'none', color: 'var(--sedra-forest)', padding: '7px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="ArrowLeft" size={12} /> {ar ? 'العودة للوحة التحكم' : 'Exit to admin'}
            </button>
          ) : (
            <button onClick={onLogout} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#eee', padding: '7px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: FONT, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="LogOut" size={12} /> {ar ? 'خروج' : 'Logout'}
            </button>
          )}
        </div>
      </div>

      {/* Impersonation banner — staff viewing a vendor's portal */}
      {impersonatedBy && (
        <div style={{ background: 'var(--sedra-forest)', color: '#fff', padding: '9px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, fontWeight: 700 }}>
          <Icon name="Eye" size={15} />
          {ar
            ? `عرض كبائع: ${vendorName} — أنت ${impersonatedBy.name || 'مشرف'}`
            : `Viewing as vendor: ${vendorName} — you are ${impersonatedBy.name || 'admin'}`}
        </div>
      )}

      <div style={{ maxWidth: 860, margin: '0 auto', padding: 24 }}>
        {suspended && (
          <div style={{ padding: '12px 18px', background: COLORS.dangerBg, color: COLORS.danger, borderRadius: 10, marginBottom: 18, fontSize: 13.5, fontWeight: 700 }}>
            {ar ? 'حسابك موقوف مؤقتًا — تواصل مع سدرة هوم.' : 'Your account is suspended — contact Sedra Home.'}
          </div>
        )}

        {savedMsg && <div style={{ padding: '10px 16px', background: COLORS.successBg, color: COLORS.success, borderRadius: 8, marginBottom: 14, fontSize: 13, fontWeight: 700 }}>{savedMsg}</div>}
        {err && <div style={{ padding: '10px 16px', background: COLORS.dangerBg, color: COLORS.danger, borderRadius: 8, marginBottom: 14, fontSize: 13, fontWeight: 700 }}>{err}</div>}

        {/* Summary cards */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
          {[
            { label: ar ? 'منتجات منشورة' : 'Live products',    value: live.length,    color: COLORS.success },
            { label: ar ? 'بانتظار المراجعة' : 'Awaiting review', value: waiting.length, color: COLORS.warn },
            { label: ar ? 'إجمالي المنتجات' : 'Total products',  value: products.length, color: COLORS.brandDark },
          ].map(c => (
            <div key={c.label} style={{ ...cardBox, flex: '1 1 140px', textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Add / edit form */}
        {form ? (
          <form onSubmit={submit} style={{ ...cardBox, marginBottom: 22 }}>
            <div style={{ fontWeight: 800, color: COLORS.brandDark, marginBottom: 14 }}>
              {form.id ? (ar ? 'تعديل منتج' : 'Edit product') : (ar ? 'إضافة منتج جديد' : 'Add new product')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <input style={input} placeholder={ar ? 'اسم المنتج (إنجليزي)' : 'Product name (EN)'} value={form.name_en} required
                onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} />
              <input style={input} placeholder={ar ? 'اسم المنتج (عربي)' : 'Product name (AR)'} value={form.name_ar}
                onChange={e => setForm(f => ({ ...f, name_ar: e.target.value }))} />
              <input style={input} placeholder="SKU" value={form.sku} required
                onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} />
              <select style={input} value={form.category} required onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                <option value="">{ar ? 'اختر الفئة…' : 'Choose category…'}</option>
                {CATEGORY_TAXONOMY.map(c => (
                  <option key={c.id} value={c.id}>{catLabel(c.id, lang)}</option>
                ))}
              </select>
              <input style={input} type="number" step="0.01" min="0" placeholder={ar ? 'سعر الجملة' : 'Wholesale price'} value={form.price} required
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
              <input style={input} type="number" min="1" placeholder={ar ? 'أقل كمية' : 'Min qty'} value={form.min_qty}
                onChange={e => setForm(f => ({ ...f, min_qty: e.target.value }))} />
              <input style={input} type="number" min="0" placeholder={ar ? 'المخزون' : 'Stock'} value={form.stock}
                onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} />
              <input style={input} type="number" min="0" placeholder={ar ? 'عدد القطع بالكرتونة' : 'Carton qty'} value={form.carton_qty || ''}
                onChange={e => setForm(f => ({ ...f, carton_qty: e.target.value }))} />
            </div>
            {/* Descriptions — parity with the admin product form */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 10 }}>
              <textarea style={{ ...input, minHeight: 68, resize: 'vertical', fontFamily: FONT }}
                placeholder={ar ? 'الوصف (إنجليزي)' : 'Description (EN)'} value={form.description || ''}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              <textarea style={{ ...input, minHeight: 68, resize: 'vertical', fontFamily: FONT, direction: 'rtl' }}
                placeholder={ar ? 'الوصف (عربي)' : 'Description (AR)'} value={form.description_ar || ''}
                onChange={e => setForm(f => ({ ...f, description_ar: e.target.value }))} />
            </div>
            <div style={{ marginTop: 12 }}>
              <ImageUploader
                value={form.image}
                onChange={url => setForm(f => ({ ...f, image: url }))}
                lang={lang}
                nameHint={form.sku || form.name_en || 'product'}
                label={ar ? 'الصورة الرئيسية' : 'Cover image'}
              />
            </div>
            {/* Gallery — extra images */}
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, color: COLORS.textMuted, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <Icon name="Camera" size={13} /> {ar ? 'صور إضافية (معرض الصور)' : 'Extra images (gallery)'}
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(Array.isArray(form.images) ? form.images : []).map((url, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <ImageUploader
                        value={url}
                        onChange={newUrl => setForm(f => { const imgs = [...(Array.isArray(f.images) ? f.images : [])]; imgs[idx] = newUrl; return { ...f, images: imgs }; })}
                        lang={lang}
                        nameHint={`${form.sku || form.name_en || 'product'}-${idx + 1}`}
                        compact
                      />
                    </div>
                    <button type="button" title={ar ? 'حذف' : 'Remove'}
                      onClick={() => setForm(f => { const imgs = [...(Array.isArray(f.images) ? f.images : [])]; imgs.splice(idx, 1); return { ...f, images: imgs }; })}
                      style={{ padding: '6px 10px', borderRadius: 6, background: COLORS.dangerBg, color: COLORS.danger, border: 'none', cursor: 'pointer', flexShrink: 0, marginTop: 4, display: 'flex' }}>
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, images: [...(Array.isArray(f.images) ? f.images : []), ''] }))}
                  style={{ padding: '7px 14px', borderRadius: 7, border: `1.5px dashed ${COLORS.brandTint}`, background: COLORS.brandWash, color: COLORS.brandDark, cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                  + {ar ? 'إضافة صورة للمعرض' : 'Add gallery image'}
                </button>
              </div>
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, margin: '12px 0' }}>
              {ar ? 'كل منتج جديد أو معدَّل يُراجع من فريق سدرة هوم قبل نشره.' : 'Every new or edited product is reviewed by Sedra Home before going live.'}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" style={button(COLORS.brand)} disabled={saveProduct.isPending}>
                {saveProduct.isPending ? '…' : (ar ? 'إرسال للمراجعة' : 'Submit for review')}
              </button>
              <button type="button" style={button(COLORS.textMuted)} onClick={() => setForm(null)}>{ar ? 'إلغاء' : 'Cancel'}</button>
            </div>
          </form>
        ) : (
          !suspended && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 22, alignItems: 'center' }}>
              <button style={{ ...button(COLORS.brand), padding: '12px 26px' }} onClick={() => { setForm({ ...EMPTY }); setSavedMsg(''); setErr(''); }}>
                ＋ {ar ? 'إضافة منتج' : 'Add product'}
              </button>
              <label style={{ ...button('#6c8ebf'), padding: '12px 20px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="FolderOpen" size={14} /> {ar ? 'استيراد منتجات (CSV)' : 'Import products (CSV)'}
                <input type="file" accept=".csv" onChange={handleCSV} style={{ display: 'none' }} />
              </label>
              <button style={{ ...button(COLORS.textMuted), padding: '12px 20px', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={downloadTemplate}>
                <Icon name="Download" size={14} /> {ar ? 'تنزيل القالب' : 'Download template'}
              </button>
              {lastImport?.newIds?.length > 0 && (
                <button style={{ ...button(COLORS.danger), padding: '12px 20px', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={undoImport} disabled={deleteMany.isPending}>
                  <Icon name="Undo2" size={14} /> {ar ? `تراجع عن الاستيراد (${lastImport.newIds.length})` : `Undo import (${lastImport.newIds.length})`}
                </button>
              )}
            </div>
          )
        )}

        {/* Import preview */}
        {importStage && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
               onClick={() => !importBusy && setImportStage(null)}>
            <div style={{ ...cardBox, maxWidth: 460, width: '100%', direction: dir }} onClick={e => e.stopPropagation()}>
              <div style={{ fontWeight: 800, fontSize: 16, color: COLORS.brandDark, marginBottom: 12 }}>
                {ar ? 'مراجعة الاستيراد' : 'Review import'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14, fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{ar ? 'منتجات جديدة' : 'New products'}</span>
                  <strong style={{ color: COLORS.success }}>{importStage.newRows.length}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{ar ? 'تحديث لمنتجات قائمة' : 'Updates to existing'}</span>
                  <strong style={{ color: COLORS.brandDark }}>{importStage.updateRows.length}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{ar ? 'صفوف بها أخطاء (تُتجاهل)' : 'Rows with errors (skipped)'}</span>
                  <strong style={{ color: importStage.errorRows.length ? COLORS.danger : COLORS.textMuted }}>{importStage.errorRows.length}</strong>
                </div>
              </div>
              {importStage.errorRows.length > 0 && (
                <div style={{ fontSize: 11.5, color: COLORS.textMuted, background: COLORS.dangerBg, padding: '8px 12px', borderRadius: 8, marginBottom: 14 }}>
                  {ar
                    ? 'الصفوف المتجاهَلة ينقصها SKU أو سعر صحيح أو فئة صحيحة. الفئات المسموحة: '
                    : 'Skipped rows are missing a SKU, a valid price, or a valid category. Allowed categories: '}
                  {CATEGORY_TAXONOMY.map(c => c.id).join(', ')}
                </div>
              )}
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 14 }}>
                {ar ? 'جميع المنتجات المستوردة تُرسَل للمراجعة وتظهر بعد موافقة سدرة هوم.' : 'All imported products are submitted for review and go live once Sedra Home approves them.'}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={button(COLORS.brand)} disabled={importBusy || (!importStage.newRows.length && !importStage.updateRows.length)} onClick={confirmImport}>
                  {importBusy ? '…' : (ar ? 'إرسال للمراجعة' : 'Submit for review')}
                </button>
                <button style={button(COLORS.textMuted)} disabled={importBusy} onClick={() => setImportStage(null)}>{ar ? 'إلغاء' : 'Cancel'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Product list */}
        <h3 style={{ margin: '0 0 10px', fontSize: 15, color: COLORS.brandDark, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="Package" size={14} /> {ar ? 'منتجاتي' : 'My products'}
        </h3>

        {/* Bulk actions bar — appears once products are selected */}
        {!suspended && selectedIds.size > 0 && (
          <div style={{ ...cardBox, marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: COLORS.brandWash }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: COLORS.brandDark, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Icon name="Check" size={14} /> {selectedIds.size} {ar ? 'محدد' : 'selected'}
            </span>
            <button style={{ ...button(COLORS.textMuted), padding: '7px 14px' }} disabled={bulkUpdate.isPending} onClick={() => bulkSetActive(false)}>
              {ar ? 'إخفاء' : 'Hide'}
            </button>
            <button style={{ ...button(COLORS.brand), padding: '7px 14px' }} disabled={bulkUpdate.isPending} onClick={() => bulkSetActive(true)}>
              {ar ? 'إظهار' : 'Show'}
            </button>

            {/* Bulk category / price — both re-enter the review queue */}
            {bulkEdit === null ? (
              <>
                <button style={{ ...button(COLORS.brandDark), padding: '7px 14px' }} disabled={bulkUpdate.isPending}
                  onClick={() => { setBulkEdit('category'); setBulkValue(''); }}>
                  {ar ? 'تغيير الفئة' : 'Set category'}
                </button>
                <button style={{ ...button(COLORS.brandDark), padding: '7px 14px' }} disabled={bulkUpdate.isPending}
                  onClick={() => { setBulkEdit('price'); setBulkValue(''); }}>
                  {ar ? 'تغيير السعر' : 'Set price'}
                </button>
              </>
            ) : (
              <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {bulkEdit === 'category' ? (
                  <select style={{ ...input, minWidth: 170 }} value={bulkValue} onChange={e => setBulkValue(e.target.value)}>
                    <option value="">{ar ? 'اختر فئة…' : 'Choose category…'}</option>
                    {CATEGORY_TAXONOMY.map(c => (
                      <option key={c.id} value={c.id}>{catLabel(c.id, lang)}</option>
                    ))}
                  </select>
                ) : (
                  <input style={{ ...input, width: 130 }} type="number" step="0.01" min="0" autoFocus
                    placeholder={ar ? 'السعر الجديد' : 'New price'}
                    value={bulkValue} onChange={e => setBulkValue(e.target.value)} />
                )}
                <button style={{ ...button(COLORS.brand), padding: '7px 14px' }}
                  disabled={bulkUpdate.isPending || !bulkValue || (bulkEdit === 'price' && !(Number(bulkValue) > 0))}
                  onClick={() => bulkEdit === 'category'
                    ? bulkEditField({ category: bulkValue }, ar ? 'الفئة' : 'Category')
                    : bulkEditField({ price: Number(bulkValue) }, ar ? 'السعر' : 'Price')}>
                  {bulkUpdate.isPending ? '…' : (ar ? 'تطبيق' : 'Apply')}
                </button>
                <button style={{ ...button('transparent'), color: COLORS.textMuted, padding: '7px 10px' }}
                  onClick={() => setBulkEdit(null)}>{ar ? 'إلغاء' : 'Cancel'}</button>
              </div>
            )}

            <button style={{ ...button('transparent'), color: COLORS.textMuted, padding: '7px 10px' }} onClick={() => { setSelectedIds(new Set()); setBulkEdit(null); }}>
              {ar ? 'إلغاء التحديد' : 'Clear'}
            </button>
          </div>
        )}
        {isLoading && <div style={{ color: COLORS.textMuted, padding: 20 }}>{ar ? 'جارٍ التحميل…' : 'Loading…'}</div>}
        {!isLoading && products.length === 0 && (
          <div style={{ ...cardBox, color: COLORS.textMuted, fontSize: 13 }}>
            {ar ? 'لم تضف منتجات بعد — ابدأ بالزر أعلاه.' : 'No products yet — start with the button above.'}
          </div>
        )}
        {products.map(p => {
          const hidden = p.is_active === false;
          const sel = selectedIds.has(p.id);
          return (
          <div key={p.id} style={{ ...cardBox, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: sel ? COLORS.brandWash : (hidden ? '#fafafa' : cardBox.background), opacity: hidden ? 0.6 : 1, border: sel ? `2px solid ${COLORS.brandTint}` : cardBox.border }}>
            {!suspended && (
              <input type="checkbox" checked={sel} onChange={e => toggleSelect(p.id, e.target.checked)}
                style={{ flexShrink: 0, width: 16, height: 16, accentColor: COLORS.brand, cursor: 'pointer' }} />
            )}
            <div style={{ width: 50, height: 50, borderRadius: 8, background: COLORS.imageWell, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.textMuted }}>
              {p.image ? <img src={p.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Icon name="Package" size={22} />}
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.brandDarker, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {ar && p.name_ar ? p.name_ar : p.name_en} {chip(p.approval_status)}
                {hidden && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 20, color: COLORS.textMuted, background: '#eee' }}>{ar ? 'مخفي' : 'Hidden'}</span>}
              </div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                {p.sku} · {fmt(p.price)} · {ar ? 'مخزون' : 'stock'} {p.stock}
              </div>
            </div>
            {!suspended && (
              <button style={{ ...button(COLORS.brand), display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => { setForm({ ...p }); setSavedMsg(''); setErr(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <Icon name="Pencil" size={12} /> {ar ? 'تعديل' : 'Edit'}
              </button>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
